import React from 'react';
import { GitBranch, Plus } from 'lucide-react';
import type { Book } from '../../../../shared/types/index.js';
import { useAuthStore } from '../../../auth/store/useAuthStore.js';
import { canCreateNewEdition } from '../book-list/bookListUtils.js';

interface BookDetailEditionBannerProps {
  book: Book;
  canEdit: boolean;
  onOpenEditionTree: () => void;
  onOpenCreateEdition: () => void;
}

export const BookDetailEditionBanner: React.FC<BookDetailEditionBannerProps> = ({
  book,
  canEdit,
  onOpenEditionTree,
  onOpenCreateEdition,
}) => {
  const { user } = useAuthStore();
  const canPublish = canCreateNewEdition(book, user);

  return (
    <div className="p-4 rounded-xl bg-gradient-to-r from-indigo-50/90 via-sky-50/50 to-white border border-indigo-200/70 shadow-2xs">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-2.5">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-lg bg-indigo-600 text-white shadow-xs">
            <GitBranch className="w-4 h-4" />
          </div>
          <div>
            <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wide">
              Hubungan Antar-Edisi (Book Edition Tree)
            </h4>
            <span className="text-[11px] text-slate-500">
              Struktur silsilah naskah orisinal, revisi berkala, cetakan baru, dan edisi terjemahan
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            id="btn-open-edition-tree-modal"
            onClick={onOpenEditionTree}
            className="px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold flex items-center gap-1.5 transition shadow-xs cursor-pointer"
          >
            <GitBranch className="w-3.5 h-3.5" />
            <span>Lihat Silsilah Lengkap</span>
          </button>

          {canPublish && (
            <button
              type="button"
              id="btn-open-create-edition-modal"
              onClick={onOpenCreateEdition}
              className="px-3 py-1.5 rounded-lg bg-white border border-emerald-400 hover:bg-emerald-50 text-emerald-800 text-xs font-semibold flex items-center gap-1.5 transition shadow-2xs cursor-pointer"
            >
              <Plus className="w-3.5 h-3.5 text-emerald-600" />
              <span>Terbitkan Edisi Baru</span>
            </button>
          )}
        </div>
      </div>

      {/* Quick edition info snippet */}
      <div className="text-xs text-slate-600 bg-white/80 p-2.5 rounded-lg border border-indigo-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-indigo-950">Status Edisi Ini:</span>
          <span className="px-2 py-0.5 rounded bg-indigo-100 text-indigo-800 font-bold text-[11px]">
            {book.edition || 'Edisi 1'}
          </span>
          {book.parentBookId && (
            <span className="text-slate-500 text-[11px]">
              (Ditautkan dari buku induk UIBN)
            </span>
          )}
        </div>

        {book.editionNotes && (
          <div className="text-[11px] text-slate-500 italic truncate max-w-sm">
            "{book.editionNotes}"
          </div>
        )}
      </div>
    </div>
  );
};
